import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { getCachedDoc } from '../../utils/docCache';
import { useLanguage } from '../../contexts/LanguageContext';
import { ScrollReveal } from '../common/ScrollReveal';
import { FaStore, FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { PARTNERS_DOC_PATH, PARTNERS_TTL, visiblePartners, partnerHref } from '../../constants/partners';

// "ร้านค้าพันธมิตร" (partner shops) band, rendered directly below the SponsorBand.
//
// Same read model as SponsorBand: ONE cached doc (public/data/site_settings/partners) through
// docCache with a 30-min TTL. The doc carries the WHOLE list, so N partners cost exactly 1
// read per visitor per TTL, and this never becomes a per-visitor live listener.
// Renders NOTHING until a visible partner (active + has artwork) exists — no empty frame.
//
// Unlike the sponsor plates (a logo on a white plate), a partner is a wide BANNER whose
// artwork already carries the shop name, so the card is the image itself (~1.85:1).
//
// LAYOUT MODEL — one scroller element for every case, so there is a single code path:
//   ≤ 5 partners, lg+  → the scroller becomes a centred flex row (5 slots), nothing scrolls.
//   > 5 partners, or below lg → horizontal scroll-snap row (78% / 40% cards so the next one
//   peeks), with left/right arrows on md+ that only show when there is somewhere to go.
// Arrow visibility is MEASURED (scrollWidth vs clientWidth), never derived from the count,
// so it stays honest across breakpoints and window resizes.
const DESKTOP_SLOTS = 5;

// Shared by PartnersPage (/partners) so both surfaces read the same cached doc and agree on
// which partners are visible and how they are named. `loaded` lets the page tell "still
// loading" from "nothing to show"; the band only needs the list.
export function usePartners() {
    const { lang } = useLanguage();
    const [raw, setRaw] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!db) { setLoaded(true); return undefined; }
        let mounted = true;
        getCachedDoc(PARTNERS_DOC_PATH, PARTNERS_TTL)
            .then((data) => { if (mounted) setRaw(data); })
            .catch(() => { })
            .finally(() => { if (mounted) setLoaded(true); });
        return () => { mounted = false; };
    }, []);

    const list = useMemo(
        () => visiblePartners(raw).map((p, i) => ({
            ...p,
            key: p.id || `${p.imageUrl}-${i}`,
            displayName: ((lang === 'en' && p.nameEn) ? p.nameEn : p.name || '').trim(),
        })),
        [raw, lang]
    );

    return { list, loaded };
}

// One banner card. With a link it is an <a> (new tab, UTM-tagged); without one it is a plain,
// non-interactive tile — no arrow, no hover lift, so it never pretends to be clickable.
// `medium` is the utm_medium, so the band and the /partners page are told apart in the
// partner's own analytics.
export function PartnerCard({ partner, fallbackAlt, eager, medium = 'home_partner', className = '' }) {
    const href = partnerHref(partner.url, medium);
    // The shop name lives inside the artwork, so this is the banner's ONLY accessible name.
    // An unnamed partner falls back to its link's hostname before the generic label, so a
    // screen reader never hears a row of identical "ร้านค้าพันธมิตร" links.
    let host = '';
    try { host = partner.url ? new URL(partner.url).hostname.replace(/^www\./, '') : ''; } catch { host = ''; }
    const label = partner.displayName || host || fallbackAlt;
    const name = partner.displayName;
    const cls = `pb-card ${href ? 'pb-card--link ' : ''}group relative block w-full aspect-[1.85/1] overflow-hidden rounded-xl ${className}`;

    const body = (
        <>
            <img
                src={partner.imageUrl}
                alt={label}
                loading={eager ? 'eager' : 'lazy'}
                decoding="async"
                draggable="false"
                className="absolute inset-0 w-full h-full object-cover"
            />
            {href && (
                <span
                    className="pb-arrow absolute right-2 bottom-2 sm:right-2.5 sm:bottom-2.5 w-7 h-7 rounded-full flex items-center justify-center text-white"
                    aria-hidden="true"
                >
                    <FaChevronRight className="text-[10px]" />
                </span>
            )}
        </>
    );

    if (!href) return <div className={cls}>{body}</div>;

    return (
        // rel is intentionally "noopener sponsored" (NOT noreferrer) — same reasoning as
        // SponsorBand: noopener blocks tabnabbing, the referrer lets the partner attribute us.
        // eslint-disable-next-line react/jsx-no-target-blank
        <a
            href={href}
            target="_blank"
            rel="noopener sponsored"
            className={cls}
            aria-label={label}
            title={name || undefined}
        >
            {body}
        </a>
    );
}

// Card + arrow styling, shared with PartnersPage. Kept as one string so the band and the page
// can never drift apart visually.
export const PARTNER_CARD_CSS = `
    /* Thin blue/indigo hairline + a soft glow in the same #3B82F6 / #6366F1 pair the sponsor
       band uses, so the two adjacent bands read as one family. */
    .pb-card{background:#EEF2FA;border:1px solid rgba(99,102,241,.28);box-shadow:0 0 0 1px rgba(59,130,246,.06),0 10px 24px -14px rgba(59,130,246,.45);transition:transform .35s cubic-bezier(0.16,1,0.3,1),box-shadow .35s cubic-bezier(0.16,1,0.3,1),border-color .25s ease}
    .pb-card--link:hover{transform:translateY(-4px);border-color:rgba(99,102,241,.55);box-shadow:0 0 0 1px rgba(99,102,241,.14),0 18px 34px -14px rgba(79,70,229,.55)}
    .pb-card--link:focus-visible{outline:2px solid #3B82F6;outline-offset:3px}
    .pb-arrow{background:rgba(15,23,42,.78);box-shadow:0 2px 8px rgba(15,23,42,.35);transition:background-color .25s ease,transform .25s ease}
    .pb-card--link:hover .pb-arrow,.pb-card--link:focus-visible .pb-arrow{background:#2563EB;transform:translateX(2px)}
    @media(prefers-reduced-motion:reduce){
        .pb-card,.pb-arrow{transition:none}
        .pb-card--link:hover,.pb-card--link:hover .pb-arrow{transform:none}
    }
`;

function PartnerBand() {
    const { t, lang } = useLanguage();
    const { list } = usePartners();
    const scrollerRef = useRef(null);
    const [canScroll, setCanScroll] = useState(false);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(false);

    const count = list.length;
    // > 5 always scrolls; ≤ 5 only scrolls below lg (the lg: classes turn it into a row).
    const alwaysScroll = count > DESKTOP_SLOTS;

    // Measure, don't guess: the arrows show only when the row really overflows on THIS screen.
    const measure = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        setCanScroll(max > 4);
        setCanPrev(el.scrollLeft > 4);
        setCanNext(max - el.scrollLeft > 4);
    }, []);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return undefined;
        measure();
        el.addEventListener('scroll', measure, { passive: true });
        window.addEventListener('resize', measure);
        // Images change nothing here (the aspect box reserves the space), but the card widths
        // depend on the container, which can change without a window resize.
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (ro) ro.observe(el);
        return () => {
            el.removeEventListener('scroll', measure);
            window.removeEventListener('resize', measure);
            if (ro) ro.disconnect();
        };
    }, [measure, count]);

    const scrollByPage = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        const reduce = typeof window !== 'undefined' && window.matchMedia
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (dir < 0 ? !canPrev : !canNext) return; // aria-disabled end — see the arrows below
        el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: reduce ? 'auto' : 'smooth' });
    };

    if (!count) return null;

    const fallbackAlt = t('partners.imageAlt');
    // Card widths: 78% on phones / 40% on tablets so the next banner peeks (the scroll cue);
    // lg = exactly 5 slots: (100% − 4 gaps of 1.25rem) / 5.
    const cardW = 'flex-none snap-start w-[78%] sm:w-[46%] md:w-[40%] lg:w-[calc((100%-5rem)/5)]';
    // The scroller bleeds into the section's side padding (−mx / px pairs) so a phone swipe
    // runs edge to edge, and the vertical padding gives the hover lift + glow room — an
    // overflow-x:auto box clips on BOTH axes (extra room below for the hover glow's tail).
    const scrollerCls = alwaysScroll
        ? 'flex gap-4 lg:gap-5 overflow-x-auto snap-x snap-mandatory'
        : 'flex gap-4 lg:gap-5 overflow-x-auto snap-x snap-mandatory lg:overflow-visible lg:justify-center lg:snap-none';

    const arrowBtn = 'pb-nav hidden md:flex absolute top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full text-gray-700 hover:text-blue-600 transition-colors';

    return (
        <section className="relative w-full overflow-hidden bg-white py-8 md:py-10 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">

                {/* HEADER — left-aligned like every other homepage section (icon + h2 + "view
                    all" link on the right), not centred like the sponsor band: this is a
                    browsable shelf, not an endorsement credit. */}
                <ScrollReveal animation="fadeLeft">
                    <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2 mb-6 md:mb-8">
                        <div className="flex items-start gap-3 min-w-0">
                            <span className="pb-tile flex-none w-11 h-11 md:w-12 md:h-12 rounded-xl flex items-center justify-center" aria-hidden="true">
                                <FaStore className="text-white text-lg md:text-xl" />
                            </span>
                            <div className="min-w-0">
                                <h2
                                    className="text-2xl md:text-3xl font-black text-gray-900"
                                    // Inline lineHeight for the same reason as SponsorBand's <h2>:
                                    // md:text-3xl would re-set any leading-* class, and Thai marks
                                    // stacked above + below need the extra room.
                                    style={{
                                        lineHeight: 1.35,
                                        ...(lang === 'en' ? { fontFamily: "'Space Grotesk', sans-serif" } : null),
                                    }}
                                >
                                    {t('partners.title')}
                                </h2>
                                <span className="pb-rule block mt-1.5" aria-hidden="true" />
                                <p className="mt-2 text-sm text-gray-500">{t('partners.subtitle')}</p>
                            </div>
                        </div>
                        {/* flex-wrap + ml-auto: on narrow phones the link drops under the title
                            (right-aligned) instead of squeezing the h2 onto two lines.
                            blue-600, not the homepage's blue-500: 4.5:1 contrast on white. */}
                        <Link
                            to="/partners"
                            className="ml-auto flex-none whitespace-nowrap text-blue-600 text-sm font-medium flex items-center gap-1 hover:text-blue-700 transition-colors"
                        >
                            {t('partners.viewAll')} <FaArrowRight className="text-xs" />
                        </Link>
                    </div>
                </ScrollReveal>

                <ScrollReveal animation="fadeUp" duration={650} delay={60}>
                    <div className="relative">
                        <div
                            ref={scrollerRef}
                            className={`pb-scroller ${scrollerCls} -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-3 lg:px-3 scroll-px-4 sm:scroll-px-6 lg:scroll-px-3 -mt-4 pt-4 -mb-8 pb-8`}
                            aria-label={t('partners.title')}
                            role="region"
                        >
                            {list.map((p, i) => (
                                <div key={p.key} className={cardW}>
                                    <PartnerCard partner={p} fallbackAlt={fallbackAlt} eager={i === 0} />
                                </div>
                            ))}
                        </div>

                        {/* Both arrows stay MOUNTED whenever the row can scroll at all; at an end the
                            arrow is only dimmed + aria-disabled (a no-op click). Unmounting it, or
                            `disabled`, would drop a keyboard user's focus to <body>. */}
                        {canScroll && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => scrollByPage(-1)}
                                    className={`${arrowBtn} left-0 -translate-x-1/2 ${canPrev ? '' : 'opacity-40 cursor-default'}`}
                                    aria-label={t('partners.prev')}
                                    aria-disabled={!canPrev}
                                >
                                    <FaChevronLeft className="text-sm" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => scrollByPage(1)}
                                    className={`${arrowBtn} right-0 translate-x-1/2 ${canNext ? '' : 'opacity-40 cursor-default'}`}
                                    aria-label={t('partners.next')}
                                    aria-disabled={!canNext}
                                >
                                    <FaChevronRight className="text-sm" />
                                </button>
                            </>
                        )}
                    </div>
                </ScrollReveal>

            </div>

            <style>{`
                ${PARTNER_CARD_CSS}
                .pb-tile{background:linear-gradient(145deg,#3B82F6 0%,#4F46E5 100%);box-shadow:0 10px 22px -12px rgba(37,99,235,.7),inset 0 1px 0 rgba(255,255,255,.25)}
                /* Short accent under the title — the sponsor band's rule, left-anchored. */
                .pb-rule{width:44px;height:3px;border-radius:9999px;background:linear-gradient(90deg,#3B82F6,#6366F1 70%,rgba(99,102,241,0))}
                @media(min-width:768px){.pb-rule{width:56px}}
                /* Swipe row: no visible scrollbar (the arrows / peeking card are the cue). */
                .pb-scroller{scrollbar-width:none;-ms-overflow-style:none;-webkit-overflow-scrolling:touch}
                .pb-scroller::-webkit-scrollbar{display:none}
                .pb-nav{background:#FFFFFF;border:1px solid #E2E8F0;box-shadow:0 6px 18px -8px rgba(15,23,42,.35)}
                .pb-nav:focus-visible{outline:2px solid #3B82F6;outline-offset:2px}
            `}</style>
        </section>
    );
}

export default PartnerBand;
